/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';

import {
  Badge,
  Button,
  Form,
  message,
  Popconfirm,
  Space,
  Table,
  Typography,
} from 'antd';

import { QueryParams, User } from '../../../types';
import { deleteUser, getUsers } from '../../../utils/httpRequest';
import Page from '../../../components/page';
import ActionHeader from '../../../components/actionHeader';
import { Content } from './styles';
import { roles } from '../../../constants';
import Icon from '@ant-design/icons';
import { SVGDelete, SVGEdit } from '../../../images';
import CreateUserModal from './createUserModal';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { fetchCompanies, selectCompanies } from '../../../slices/companies';
import { useAppDispatch } from '../../../store';

type UserListParams = QueryParams & {
  templateId?: number;
};

const UserList = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const companies = useSelector(selectCompanies);
  const [messageApi, contextHolder] = message.useMessage();

  const [params, setParams] = useState<UserListParams>({
    search: '',
    sortBy: 'createdTime',
    sortDirection: 'ASC',
    pageNumber: 0,
    pageSize: 10,
  });

  const editingUser = users.find((i) => i.userId === editingUserId);

  const handleAddUser = () => {
    setEditingUserId('');
    setShowCreateUserModal(true);
  };

  const handleEditUser = async (record: User) => {
    setShowCreateUserModal(true);
    setEditingUserId(record.userId);
  };

  const getData = async () => {
    try {
      setLoading(true);
      const res = await getUsers(params);
      setUsers(
        (res?.data || []).map((i: User) => ({ ...i, role: i.roles[0].name }))
      );
      setTotal(res?.totalNumber || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setLoading(true);
      await deleteUser(userId);
      getData();
    } catch (err: any) {
      messageApi.error(err || 'Delete failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchText?: string) => {
    setParams((prev) => ({ ...prev, search: searchText || '', pageNumber: 0 }));
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setParams((prev) => ({
      ...prev,
      pageNumber: pagination.current - 1,
      pageSize: pagination.pageSize,
      sortBy: sorter.field,
      sortDirection: sorter.order === 'ascend' ? 'ASC' : 'DESC',
    }));
  };

  useEffect(() => {
    getData();
    if (!companies.length) {
      dispatch(fetchCompanies());
    }
  }, []);

  useEffect(() => {
    getData();
  }, [params]);

  const columns = [
    {
      title: 'Full Name',
      width: 240,
      render: (_value: any, record: User) =>
        `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-',
    },
    {
      title: 'Company',
      width: 240,
      dataIndex: 'companyName',
      render: (value: string, record: User) => record?.companyName || '-',
    },
    { title: 'Email', width: 240, dataIndex: 'email', sorter: true },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (value: any) => {
        return roles.find((i) => i.value === value)?.label;
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: (value: boolean) => {
        return (
          <Badge
            color={value ? '#52c41a' : '#ff4d4f'}
            text={value ? 'Active' : 'Deactivated'}
          />
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (value: any, record: User, index: number) => {
        return (
          <Space>
            <Typography.Link onClick={() => handleEditUser(record)}>
              <Icon component={SVGEdit} /> Edit
            </Typography.Link>
            <Popconfirm
              placement="bottom"
              title={`Are you sure you want to delete ${record.username}?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDeleteUser(record.userId)}
            >
              <Typography.Link type="danger" title="Delete">
                <Icon component={SVGDelete} />
              </Typography.Link>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Page
      header={
        <ActionHeader
          title="User Management"
          titleTag={`(${total})`}
          endSlot={
            <Button type="primary" onClick={handleAddUser}>
              Add New User
            </Button>
          }
          showSearch
          onSearch={handleSearch}
        />
      }
    >
      <Content>
        {contextHolder}
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={users.map((i) => ({ key: i.userId, ...i }))}
            loading={loading}
            pagination={{
              current: (params.pageNumber || 0) + 1,
              pageSize: params.pageSize,
              total,
            }}
            onChange={handleTableChange}
          ></Table>
        </Form>
        <CreateUserModal
          open={showCreateUserModal}
          onClose={() => {
            setShowCreateUserModal(false);
            setEditingUserId('');
          }}
          onConfirm={() => {
            setShowCreateUserModal(false);
            getData();
            setEditingUserId('');
          }}
          isEdit={!!editingUserId}
          user={editingUser}
        />
      </Content>
    </Page>
  );
};

export default UserList;
